import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { useEffect } from 'react';
import { PlanType, Perk, SUBSCRIPTION_BENEFITS } from 'modules/Const';
import Axios from 'helpers/Interceptor';

export interface PlanPricing {
  monthly: {
    cents: number;
    formatted: string;
    currency: string;
  };
  annual: {
    cents: number;
    formatted: string;
    perMonthCents: number;
    currency: string;
  };
}

export interface PlanFeatures {
  messageLimit: number;
  periodType: 'daily' | 'weekly' | 'monthly';
  takeRatePercentage: number;
  takeRateFormatted: string;
  minimumFeeCents: number;
  minimumFeeFormatted: string;
}

export interface PlanStripeIds {
  monthly: string;
  annual: string;
}

export interface PlanLegacy {
  monthlyPrice: number;
  annualPrice: number;
  feePercentage: number;
  messageCredits: number; // For backward compatibility
}

export interface DynamicPlan {
  planType: PlanType;
  pricing: PlanPricing;
  features: PlanFeatures;
  stripeIds: PlanStripeIds;
  legacy: PlanLegacy;
}

export interface PlansResponse {
  plans: DynamicPlan[];
  currency: string;
  newPricingEnabled: boolean;
  cacheUsed: boolean;
  lastUpdated: string;
}


interface PlansState {
  /** Current plans data */
  plans: DynamicPlan[];
  isLoading: boolean;
  error: string | null;
  isNewPricingEnabled: boolean;
  currency: string;
  lastUpdated: string | null;

  /** Auto-refresh settings */
  autoRefresh: boolean;
  refreshInterval: number;
  refreshIntervalId: NodeJS.Timeout | null;

  /** Actions */
  fetchPlans: (options?: {
    currency?: string;
    useCache?: boolean;
  }) => Promise<void>;
  clearCache: (planType?: PlanType, currency?: string) => Promise<void>;

  /** Helper functions for convenience */
  getPlan: (planType: PlanType) => DynamicPlan | undefined;
  getMonthlyPrice: (planType: PlanType) => number;
  getAnnualPrice: (planType: PlanType) => number;
  getTakeRatePercentage: (planType: PlanType) => number;
  getMessageLimit: (planType: PlanType) => number;
  generatePerksFromPlan: (planType: PlanType, plan?: DynamicPlan) => Perk[];

  /** Computed values */
  isInitialized: () => boolean;
}

export interface PlansExistsState extends PlansState {
  plans: DynamicPlan[];
}

const usePlans = create(immer<PlansState>((set, get) => ({
  plans: [],
  isLoading: false,
  error: null,
  isNewPricingEnabled: false,
  currency: 'USD',
  lastUpdated: null,
  autoRefresh: false,
  refreshInterval: 5 * 60 * 1000, // 5 minutes default
  refreshIntervalId: null,

  fetchPlans: async (options: {
    currency?: string;
    useCache?: boolean;
  } = {}) => {
    const {
      useCache = true,
      currency = 'USD'
    } = options;

    try {
      if (get().isLoading) return;
      set(state => {
        state.isLoading = true;
        state.error = null;
        state.currency = currency;
      });

      const response = await Axios.get('/v2/payments/plans', {
        params: {
          currency,
          useCache: useCache.toString()
        }
      });

      if (response.data.plans) {
        const plansData: PlansResponse = response.data;

        set(state => {
          state.plans = plansData.plans;
          state.isNewPricingEnabled = plansData.newPricingEnabled;
          state.lastUpdated = plansData.lastUpdated;
        });
      } else {
        throw new Error(response.data.message || 'Failed to fetch plans');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch plans data';

      set(state => {
        state.error = errorMessage;
      });

      console.error('Error fetching plans:', err);

      // If new pricing fails, you could implement fallback to legacy data here
      if (!get().isNewPricingEnabled) {
        console.warn('New pricing not enabled, falling back to legacy system');
      }
    } finally {
      set(state => {
        state.isLoading = false;
      });
    }
  },

  clearCache: async (planType?: PlanType, currency?: string) => {
    const currentCurrency = currency || get().currency;

    try {
      const endpoint = planType
        ? `/v2/payments/plans/cache/${planType}`
        : '/v2/payments/plans/cache';

      await Axios.delete(endpoint, {
        params: { currency: currentCurrency }
      });

      // Refresh data after clearing cache
      await get().fetchPlans({ currency: currentCurrency });
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to clear cache';

      set(state => {
        state.error = errorMessage;
      });

      console.error('Error clearing plan cache:', err);
    }
  },

  // Helper functions for convenience
  getPlan: (planType: PlanType): DynamicPlan | undefined => {
    return get().plans.find(plan => plan.planType === planType);
  },

  getMonthlyPrice: (planType: PlanType): number => {
    const plan = get().getPlan(planType);
    return plan ? plan.pricing.monthly.cents / 100 : 0;
  },

  getAnnualPrice: (planType: PlanType): number => {
    const plan = get().getPlan(planType);
    return plan ? plan.pricing.annual.cents / 100 : 0;
  },

  getTakeRatePercentage: (planType: PlanType): number => {
    const plan = get().getPlan(planType);
    return plan ? plan.features.takeRatePercentage : 0;
  },

  getMessageLimit: (planType: PlanType): number => {
    const plan = get().getPlan(planType);
    return plan ? plan.features.messageLimit : 0;
  },

  generatePerksFromPlan: (planType: PlanType, plan?: DynamicPlan): Perk[] => {
    const planData = plan || get().getPlan(planType);
    
    // Get base perks from SUBSCRIPTION_BENEFITS to avoid duplication
    const basePerks = SUBSCRIPTION_BENEFITS[planType]?.perks || [];
    
    // Create a copy of base perks and update dynamic values
    const dynamicPerks = basePerks.map(perk => {
      // Update AI Messages with dynamic message limit
      if (perk.label === 'AI credit' || perk.label === 'AI credits' || perk.label === 'AI Messages') {
        return {
          ...perk,
          numeric: planData?.features.messageLimit?.toString() || perk.numeric || '5',
          label: 'AI Messages', // Update terminology consistently
        };
      }
      
      // Update Automation Fee with dynamic take rate
      if (perk.label === 'Automation Fee on Ads') {
        return {
          ...perk,
          numeric: planData?.features.takeRateFormatted || perk.numeric || '15%',
        };
      }
      
      return perk;
    });

    // Add any plan-specific perks that aren't in SUBSCRIPTION_BENEFITS
    if (planType === PlanType.GROWTH) {
      // Add Advanced Maestro Skills if not already present
      const hasAdvancedMaestro = dynamicPerks.some(perk => 
        perk.label === 'Advanced Maestro Skills'
      );
      if (!hasAdvancedMaestro) {
        // Insert after AI Messages
        const aiMessageIndex = dynamicPerks.findIndex(perk => perk.label === 'AI Messages');
        dynamicPerks.splice(aiMessageIndex + 1, 0, {
          icon: 'robot',
          label: 'Advanced Maestro Skills',
          subtitle: 'Marketing Plans, Content Analysis, Memory, & More'
        });
      }
    }

    return dynamicPerks;
  },

  // Computed values
  isInitialized: (): boolean => {
    const { plans, isLoading, error } = get();
    return plans.length > 0 && (!isLoading && error === null);
  }
})));

export default usePlans; 